import type { DashboardActivityKind } from '../types/models';

/** Zeettu-only route prefixes (longest match first). */
const ZEETTU_MODULE_PREFIXES = ['/reports/zdr', '/delivery-packages', '/zeettu'].sort(
  (a, b) => b.length - a.length,
);

/** Installment-only route prefixes (longest match first). */
const INSTALLMENT_MODULE_PREFIXES = [
  '/installment-cards',
  '/reports/recovery-dashboard',
  '/reports/irr',
  '/reports/pdr',
  '/samples',
  '/audit',
].sort((a, b) => b.length - a.length);

const ZEETTU_ACTIVITY_KINDS = new Set<DashboardActivityKind>(['zdr']);
const INSTALLMENT_ACTIVITY_KINDS = new Set<DashboardActivityKind>(['pdr', 'irr']);

let runtimeZeettuEnabled = true;
let runtimeInstallmentEnabled = true;

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function matchesAnyPrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

export function resolveModuleFlag(value: boolean | undefined): boolean {
  return value !== false;
}

export function setModuleFlags(config: {
  isZeettuEnabled?: boolean;
  isInstallmentEnabled?: boolean;
}): void {
  runtimeZeettuEnabled = resolveModuleFlag(config.isZeettuEnabled);
  runtimeInstallmentEnabled = resolveModuleFlag(config.isInstallmentEnabled);
}

export function getModuleFlags(): { isZeettuEnabled: boolean; isInstallmentEnabled: boolean } {
  return {
    isZeettuEnabled: runtimeZeettuEnabled,
    isInstallmentEnabled: runtimeInstallmentEnabled,
  };
}

export function isZeettuModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, ZEETTU_MODULE_PREFIXES);
}

export function isInstallmentModulePath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  return matchesAnyPrefix(path, INSTALLMENT_MODULE_PREFIXES);
}

/** False when the path belongs to a disabled module; true for shared paths and enabled modules. */
export function isModuleEnabledForPath(pathname: string): boolean {
  const path = pathname.split('?')[0] || '/';
  if (isZeettuModulePath(path) && !runtimeZeettuEnabled) {
    return false;
  }
  if (isInstallmentModulePath(path) && !runtimeInstallmentEnabled) {
    return false;
  }
  return true;
}

/** Dashboard recent-activity feed: filter report rows by module flags. */
export function isDashboardActivityKindEnabled(kind: DashboardActivityKind): boolean {
  if (ZEETTU_ACTIVITY_KINDS.has(kind)) {
    return runtimeZeettuEnabled;
  }
  if (INSTALLMENT_ACTIVITY_KINDS.has(kind)) {
    return runtimeInstallmentEnabled;
  }
  return true;
}
